import { inferInvoiceType, calcInvoiceTotals } from './invoiceTax';

export function catalogServiceKeyFromTask(task) {
  const remainder = String(task?.remainder || '').trim();
  if (remainder.startsWith('svc:')) return remainder.slice(4);
  const desc = String(task?.description || task?.taskDescription || '');
  const idLine = desc.match(/^Service:([0-9a-f-]{36})\s*$/im);
  if (idLine) return idLine[1];
  return '';
}

export function displayTaskNotes(description) {
  return String(description || '')
    .split('\n')
    .map((line) => line.trim())
    .filter(
      (line) =>
        line &&
        !/^Service:/i.test(line) &&
        !/^Requirement:/i.test(line) &&
        !/^Role:/i.test(line) &&
        !/^Frequency:/i.test(line) &&
        // Internal-only lines; never print on a client document.
        !/^Group:/i.test(line) &&
        !/^Instructions:/i.test(line),
    )
    .join('\n');
}

export function parseTaskMeta(task) {
  const desc = String(task?.taskDescription ?? task?.description ?? '');
  const requirement = (desc.match(/^Requirement:\s*(.+)$/im) || [])[1] || '';
  const assignedRole = (desc.match(/^Role:\s*(.+)$/im) || [])[1] || '';
  const frequency = (desc.match(/^Frequency:\s*(.+)$/im) || [])[1] || '';
  return {
    requirement: String(requirement).toUpperCase(),
    assignedRole: String(assignedRole).trim(),
    frequency: String(frequency).trim(),
  };
}

/** Description column only — strips metadata and legacy "title — description". */
export function taskDescriptionOnly(task) {
  const title = String(task?.title || task?.taskTitle || '').trim();
  const raw = String(task?.taskDescription ?? task?.description ?? '').trim();
  if (!raw) return '';
  let next = raw;
  const prefix = `${title} — `;
  if (title && raw.startsWith(prefix)) next = raw.slice(prefix.length).trim();
  return displayTaskNotes(next);
}

export function splitTaskDescriptionPoints(text) {
  return String(text || '')
    .split(/\n+/)
    .map((line) =>
      line.replace(/^[-*•]+\s*/, '').replace(/^\d+[.)]\s*/, '').trim(),
    )
    .filter(Boolean);
}

export function resolveTaskNotesFormat(task, line) {
  const explicit = String(
    task?.notesFormat || task?.taskNotesFormat || line?.taskNotesFormat || '',
  ).toLowerCase();
  if (explicit === 'bullets' || explicit === 'normal') return explicit;
  const raw = taskDescriptionOnly(task);
  const lines = String(raw || '')
    .split(/\n+/)
    .map((item) => item.trim())
    .filter(Boolean);
  if (lines.length >= 2) return 'bullets';
  if (lines.some((item) => /^[-*•]/.test(item) || /^\d+[.)]/.test(item))) {
    return 'bullets';
  }
  return 'normal';
}

export function isBulletTaskNotes(task, line) {
  return resolveTaskNotesFormat(task, line) === 'bullets';
}

export function isVisitExtraGroup(group) {
  const id = String(group?.serviceId || group?.service?.id || '');
  return (
    id.startsWith('visit-extra:') ||
    String(group?.priceSource || '') === 'visit-extra'
  );
}

export function extraGroupsFromVisitExtras(visitExtras) {
  const rows = Array.isArray(visitExtras) ? visitExtras : [];
  return rows
    .map((row, index) => {
      const amount = Math.max(0, Number(row?.amount) || 0);
      if (!(amount > 0)) return null;
      const note = String(row?.note || '').trim() || 'Visit extra';
      const id = `visit-extra:${index}`;
      return {
        serviceId: id,
        serviceName: 'Visit extra',
        frequency: 'This visit',
        pricingMode: 'task',
        servicePrice: 0,
        serviceDiscount: 0,
        serviceTotal: amount,
        priceSource: 'visit-extra',
        service: { id, service: 'Visit extra' },
        tasks: [
          {
            taskId: `${id}-task`,
            title: note,
            taskTitle: note,
            description: note,
            taskDescription: note,
            price: amount,
            discount: 0,
            total: amount,
            priceSource: 'visit-extra',
          },
        ],
      };
    })
    .filter(Boolean);
}

export function groupsFromProjectServices(projectServices) {
  const rows = Array.isArray(projectServices) ? projectServices : [];
  return rows
    .map((ps, index) => {
      const serviceId = String(
        ps?.service?.id || ps?.catalogServiceId || '',
      ).trim();
      const serviceName = String(
        ps?.service?.service || ps?.serviceName || ps?.name || '',
      ).trim();
      if (!serviceId && !serviceName) return null;
      const planned = Array.isArray(ps?.tasks) ? ps.tasks : [];
      const pricingMode =
        String(ps?.pricingMode || ps?.linePricingMode || '').toLowerCase() ===
        'task'
          ? 'task'
          : 'service';
      const tasks = planned
        .map((t) => {
          const title = String(t?.title || t?.taskTitle || '').trim();
          if (!title) return null;
          const price = Math.max(0, Number(t?.price) || 0);
          const discount = Math.min(100, Math.max(0, Number(t?.discount) || 0));
          return {
            taskId: t?.taskId || '',
            title,
            taskTitle: title,
            description: t?.description || t?.taskDescription || '',
            taskDescription: t?.description || t?.taskDescription || '',
            notesFormat: resolveTaskNotesFormat(t),
            price,
            discount,
            total: Number(t?.total) || price * (1 - discount / 100),
            priceSource: 'manual',
          };
        })
        .filter(Boolean);
      const taskTotal = tasks.reduce(
        (sum, t) => sum + (Number(t.total) || 0),
        0,
      );
      const servicePrice =
        pricingMode === 'task' ? 0 : Number(ps?.total ?? ps?.price) || 0;
      return {
        serviceId: serviceId || `project-svc-${index}`,
        serviceName: serviceName || `Service ${index + 1}`,
        frequency: ps?.frequency || 'One-time',
        pricingMode,
        servicePrice,
        serviceDiscount: 0,
        serviceTotal: pricingMode === 'task' ? taskTotal : servicePrice,
        service: {
          id: serviceId || `project-svc-${index}`,
          service: serviceName || `Service ${index + 1}`,
        },
        tasks,
      };
    })
    .filter(Boolean);
}

export function ensureInvoiceHasBaseServices(invoice, projectServices) {
  if (!invoice) return invoice;
  const groups = Array.isArray(invoice?.services?.groups)
    ? invoice.services.groups
    : [];
  const real = groups.filter((g) => !isVisitExtraGroup(g));
  if (real.length) return invoice;
  const fromProject = groupsFromProjectServices(projectServices);
  if (!fromProject.length) return invoice;
  const extras = groups.filter(isVisitExtraGroup);
  return {
    ...invoice,
    services: {
      ...(invoice.services || {}),
      groups: [...fromProject, ...extras],
    },
  };
}

export function mergeVisitExtrasIntoInvoice(invoice, visitExtras) {
  if (!invoice) return invoice;
  if (visitExtras == null) return invoice;
  const extras = extraGroupsFromVisitExtras(visitExtras);
  const groups = (
    Array.isArray(invoice?.services?.groups) ? invoice.services.groups : []
  ).filter((g) => !isVisitExtraGroup(g));
  const data = (
    Array.isArray(invoice?.services?.data) ? invoice.services.data : []
  ).filter(
    (row) =>
      !isVisitExtraGroup(row) && String(row?.priceSource || '') !== 'visit-extra',
  );
  const extraData = extras.flatMap((g) => [
    {
      id: `${g.serviceId}-service`,
      frequency: g.frequency,
      discount: 0,
      price: 0,
      total: g.serviceTotal,
      description: g.serviceName,
      priceSource: 'visit-extra',
      pricingMode: 'task',
      service: g.service,
    },
    ...(g.tasks || []).map((t) => ({
      id: t.taskId,
      frequency: g.frequency,
      discount: 0,
      price: t.price,
      total: t.total,
      title: t.title,
      taskTitle: t.title,
      description: t.description,
      taskDescription: t.description,
      priceSource: 'visit-extra',
      pricingMode: 'task',
      service: g.service,
    })),
  ]);
  const mergedGroups = [...groups, ...extras];
  const lineSum = mergedGroups.reduce(
    (sum, g) => sum + (Number(g.serviceTotal) || 0),
    0,
  );
  const invoiceType = inferInvoiceType(invoice);
  const { grandTotal, subtotalExGst } = calcInvoiceTotals(lineSum, invoiceType);
  const amountPaid = Number(invoice?.amountPaid) || 0;
  return {
    ...invoice,
    services: {
      ...(invoice.services || {}),
      groups: mergedGroups,
      data: [...data, ...extraData],
      totalPrice: subtotalExGst,
      totalWithTax: grandTotal,
    },
    totalAmount: grandTotal,
    amountDue: Math.max(0, grandTotal - amountPaid),
    visitExtras: extras.map((g) => ({
      amount: g.serviceTotal,
      note: g.tasks?.[0]?.title || g.serviceName,
    })),
  };
}

/** Map API task rows to invoice line items (included under service package). */
export function projectTasksToInvoiceLines(projectTasks) {
  const list = Array.isArray(projectTasks) ? projectTasks : [];
  return list.map((task) => {
    const title = String(task?.title || '').trim();
    const description = String(task?.description || '').trim();
    return {
      taskId: task?.id || '',
      title,
      taskTitle: title,
      description,
      taskDescription: description,
      remainder: task?.remainder || '',
      notesFormat: resolveTaskNotesFormat(task),
      price: 0,
      discount: 0,
      total: 0,
      priceSource: 'manual',
    };
  });
}

export function groupNeedsTasks(group) {
  const tasks = Array.isArray(group?.tasks) ? group.tasks : [];
  if (!tasks.length) return true;
  return !tasks.some((t) => String(t?.taskId || '').trim());
}

export function groupsShareSameTaskIds(groups) {
  if (!Array.isArray(groups) || groups.length < 2) return false;
  const keys = groups.map((g) =>
    (g.tasks || [])
      .map((t) => String(t?.taskId || t?.title || '').trim())
      .filter(Boolean)
      .sort()
      .join(','),
  );
  return keys.every((key) => key && key === keys[0]);
}

export function snapshotTasksForService(projectServices, serviceId) {
  let rows = projectServices;
  if (typeof rows === 'string') {
    try {
      rows = JSON.parse(rows);
    } catch {
      rows = [];
    }
  }
  rows = Array.isArray(rows) ? rows : [];
  const row = rows.find(
    (item) => String(item?.service?.id || '') === String(serviceId || ''),
  );
  return (Array.isArray(row?.tasks) ? row.tasks : []).filter((task) =>
    String(task?.title || '').trim(),
  );
}

export function invoiceNeedsTaskHydration(invoice) {
  const groups = invoice?.services?.groups;
  if (!Array.isArray(groups) || !groups.length) return false;
  const real = groups.filter((g) => !isVisitExtraGroup(g));
  if (!real.length) return false;
  return real.some(groupNeedsTasks) || groupsShareSameTaskIds(real);
}

function linesFromSnapshot(snapshot) {
  return snapshot.map((task) => ({
    taskId: '',
    title: task.title,
    taskTitle: task.title,
    description: task.description || '',
    taskDescription: task.description || '',
    notesFormat: resolveTaskNotesFormat(task),
    price: 0,
    discount: 0,
    total: 0,
    priceSource: 'manual',
  }));
}

/**
 * Fill each invoice service group with that service's tasks only.
 * Fixes older invoices that copied every project task onto every service.
 */
export function hydrateInvoiceWithTasks(
  invoice,
  projectTasks,
  projectServices = [],
) {
  if (!invoice) return invoice;

  const groups = Array.isArray(invoice?.services?.groups)
    ? invoice.services.groups
    : [];
  if (!groups.length) return invoice;

  const identicalDump = groupsShareSameTaskIds(groups);
  const list = Array.isArray(projectTasks) ? projectTasks : [];

  const nextGroups = groups.map((g) => {
    if (isVisitExtraGroup(g)) return g;
    const serviceId = String(g.serviceId || g.service?.id || '').trim();
    const keepPrices = (tasks) => {
      const current = Array.isArray(g.tasks) ? g.tasks : [];
      return (Array.isArray(tasks) ? tasks : []).map((task, index) => {
        const match =
          current.find(
            (row) =>
              String(row?.taskId || '') === String(task?.taskId || '') ||
              String(row?.title || '')
                .trim()
                .toLowerCase() ===
                String(task?.title || task?.taskTitle || '')
                  .trim()
                  .toLowerCase(),
          ) || current[index];
        if (!match) return task;
        return {
          ...task,
          notesFormat:
            match.notesFormat ||
            match.taskNotesFormat ||
            task.notesFormat ||
            resolveTaskNotesFormat(task),
          price: Number(match.price) || Number(task.price) || 0,
          discount: Number(match.discount) || Number(task.discount) || 0,
          total:
            Number(match.total) ||
            Number(match.price) ||
            Number(task.total) ||
            0,
        };
      });
    };
    const stored = Array.isArray(g.tasks) ? g.tasks : [];
    const storedHasContent = stored.some((task) =>
      String(
        task?.title ||
          task?.taskTitle ||
          task?.description ||
          task?.taskDescription ||
          '',
      ).trim(),
    );
    if (storedHasContent && !identicalDump) {
      return g;
    }

    const tagged = serviceId
      ? list.filter((task) => catalogServiceKeyFromTask(task) === serviceId)
      : [];
    if (tagged.length) {
      return { ...g, tasks: keepPrices(projectTasksToInvoiceLines(tagged)) };
    }

    const snapshot = snapshotTasksForService(projectServices, serviceId);
    if (snapshot.length) {
      if (!groupNeedsTasks(g) && !identicalDump) return g;
      return { ...g, tasks: keepPrices(linesFromSnapshot(snapshot)) };
    }

    if (identicalDump) {
      return { ...g, tasks: [] };
    }

    return g;
  });

  return {
    ...invoice,
    services: {
      ...(invoice.services || {}),
      groups: nextGroups,
    },
  };
}

function taskTitleKey(task) {
  return String(task?.title || task?.taskTitle || task?.name || '')
    .trim()
    .toLowerCase();
}

function selectedProjectTitles(group, projectTasks, projectServices, singleService) {
  const keys = new Set();
  snapshotTasksForService(
    projectServices,
    group?.serviceId || group?.service?.id,
  ).forEach((task) => {
    const key = taskTitleKey(task);
    if (key) keys.add(key);
  });
  const serviceId = String(group?.serviceId || group?.service?.id || '').trim();
  const serviceName = String(
    group?.serviceName || group?.service?.service || '',
  )
    .trim()
    .toLowerCase();
  const list = Array.isArray(projectTasks) ? projectTasks : [];
  const live = singleService
    ? list
    : list.filter((task) => {
        const key = catalogServiceKeyFromTask(task);
        if (serviceId && key === serviceId) return true;
        if (key || !serviceName) return false;
        const hay = `${task?.title || ''} ${task?.description || ''}`.toLowerCase();
        return hay.includes(serviceName);
      });
  live.forEach((task) => {
    const key = taskTitleKey(task);
    if (key) keys.add(key);
  });
  return keys;
}

/** Drop catalogue tasks that were not selected on the quotation or project. */
export function keepSelectedProjectTasks(invoice, projectTasks, projectServices) {
  if (!invoice) return invoice;
  const kind = String(
    invoice?.invoiceKind || invoice?.services?.invoiceKind || '',
  )
    .trim()
    .toUpperCase();
  if (kind === 'CUSTOM') return invoice;
  const groups = Array.isArray(invoice?.services?.groups)
    ? invoice.services.groups
    : [];
  if (!groups.length) return invoice;
  const billable = groups.filter((group) => !isVisitExtraGroup(group));
  const singleService = billable.length <= 1;
  let changed = false;
  const nextGroups = groups.map((group) => {
    if (isVisitExtraGroup(group)) return group;
    const keys = selectedProjectTitles(
      group,
      projectTasks,
      projectServices,
      singleService,
    );
    if (!keys.size) return group;
    const tasks = (Array.isArray(group.tasks) ? group.tasks : []).filter(
      (task) => {
        const key = taskTitleKey(task);
        if (!key) return false;
        if (keys.has(key)) return true;
        return (Number(task?.price) || Number(task?.total) || 0) > 0;
      },
    );
    if (tasks.length === (group.tasks || []).length) return group;
    changed = true;
    return { ...group, tasks };
  });
  if (!changed) return invoice;
  return {
    ...invoice,
    services: {
      ...(invoice.services || {}),
      groups: nextGroups,
    },
  };
}
